import * as Yup from 'yup'
import { transformFormValues } from '../../shared/formFunctions'
import { IEndOfLife, IEndOfLifeConfiguration, IEndOfLifeMutation } from './types'

export const DISPOSAL_RATE_ERROR = 'Incineration and Landfill rates must add up to exactly 100.'

export enum TabsEnum {
  COUNTRY_INFO = 'COUNTRY_INFO',
  RECYCLING_RATES = 'RECYCLING_RATES',
  WASTE_DISPOSAL = 'WASTE_DISPOSAL',
  DATA_QUALITY = 'DATA_QUALITY',
}

export const TAB_LABELS = {
  [TabsEnum.COUNTRY_INFO]: 'Country info',
  [TabsEnum.RECYCLING_RATES]: 'Recycling rates',
  [TabsEnum.WASTE_DISPOSAL]: 'Waste disposal split',
  [TabsEnum.DATA_QUALITY]: 'Data quality',
}

export const TAB_ERRORS = {
  [TabsEnum.COUNTRY_INFO]: ['countryId', 'regionIdentifierId', 'productTaxonomyLevel', 'productId'],
  [TabsEnum.RECYCLING_RATES]: ['recyclingRates'],
  [TabsEnum.WASTE_DISPOSAL]: ['disposalIncinerationRate', 'disposalLandfillRate'],
  [TabsEnum.DATA_QUALITY]: [
    'secondaryDataReferenceSourceType',
    'referencesSamplingYear',
    'referenceDataPublicationYear',
    'referenceRegionIdentifier',
  ],
}

export const toNumberOrNull = (value: unknown): number | null =>
  value === '' || value === null || value === undefined || Number(value) < 0 ? null : Number(value)

// EndOfLife is packaging-only today, but the enum will gain members. Reading the product type from
// the configuration means one member renders read-only and a second turns the field into a dropdown
// with no FE change. Never hardcode the numeric value: PackagingMaterial is 1 on
// EndOfLifeProductTypeEnum, not the 4 that ProductTypeEFEnum uses for emission factors.
export const resolveProductType = (configuration: IEndOfLifeConfiguration): number | null =>
  configuration.productType?.length === 1 ? configuration.productType[0].id : null

export const isSingleProductType = (configuration: IEndOfLifeConfiguration): boolean =>
  configuration.productType?.length === 1

// Levels come from the configuration, so adding Subcategory or a food level BE-side needs no FE
// change. The names are matched case- and space-insensitively because the enum descriptions are
// human-readable labels ("Functional unit"), not identifiers.
const normalise = (value: string | null | undefined): string =>
  (value ?? '').toLowerCase().replace(/[^a-z]/g, '')

export const findTaxonomyLevelId = (
  configuration: IEndOfLifeConfiguration,
  name: string
): number | null =>
  configuration.productTaxonomyLevel?.find(
    level =>
      normalise(level.name) === normalise(name) || normalise(level.description) === normalise(name)
  )?.id ?? null

// The (productType, productTaxonomyLevel) pair decides which taxonomy list the Product select
// offers. Today only packaging resolves; a food product type would add its own branch here.
export const getProductOptions = (
  configuration: IEndOfLifeConfiguration,
  productTaxonomyLevel: number | null
): { id: number; name: string }[] => {
  if (productTaxonomyLevel === null) {
    return []
  }
  if (productTaxonomyLevel === findTaxonomyLevelId(configuration, 'Category')) {
    return configuration.packagingMaterialCategories ?? []
  }
  if (productTaxonomyLevel === findTaxonomyLevelId(configuration, 'FunctionalUnit')) {
    return configuration.packagingMaterialFunctionalUnits ?? []
  }
  return []
}

// Only editable current-contract fields may cross the mutation boundary. Never spread a GET
// response: generated metadata, legacy fields and shared-section extras are not writable.
const END_OF_LIFE_WRITABLE_FIELDS = [
  'companyId',
  'countryId',
  'regionIdentifierId',
  'productType',
  'productTaxonomyLevel',
  'productId',
  'recyclingRates',
  'dataSourceComment',
  'disposalDataSourceComment',
  'disposalIncinerationRate',
  'disposalLandfillRate',
  'secondaryDataReferenceSourceType',
  'dataQualityReliabilityScore',
  'referencesSamplingYear',
  'referenceDataPublicationYear',
  'referenceRegionIdentifier',
  'referenceSourcingCountry',
  'dataQualityComment',
  'referenceTechnologyType',
  'referenceTechnologyProxyContribution',
  'isVerified',
  'verificationComments',
] as const

export function stripLegacyEndOfLifeFields(source: IEndOfLife): IEndOfLife {
  const {
    disposalIncinerationProcessingActivityId: _incineration,
    disposalLandfillProcessingActivityId: _landfill,
    ...record
  } = source as IEndOfLife & Record<string, unknown>
  return record
}

export function transformEndOfLifeValues(
  values: IEndOfLife,
  configuration: IEndOfLifeConfiguration
): IEndOfLifeMutation {
  const writable = Object.fromEntries(END_OF_LIFE_WRITABLE_FIELDS.map(key => [key, values[key]]))
  const transformed = transformFormValues(writable)
  return {
    ...transformed,
    // Preserve textual values, even when they look like numbers or an empty string.
    companyId: values.companyId ?? null,
    dataSourceComment: values.dataSourceComment ?? null,
    disposalDataSourceComment: values.disposalDataSourceComment ?? null,
    dataQualityComment: values.dataQualityComment ?? null,
    referenceTechnologyType: values.referenceTechnologyType ?? null,
    verificationComments: values.verificationComments ?? null,
    isVerified: values.isVerified,
    productType: resolveProductType(configuration) ?? toNumberOrNull(values.productType),
    countryId: toNumberOrNull(values.countryId),
    regionIdentifierId: toNumberOrNull(values.regionIdentifierId),
    productTaxonomyLevel: toNumberOrNull(values.productTaxonomyLevel),
    productId: toNumberOrNull(values.productId),
    recyclingRates:
      values.recyclingRates == null || (values.recyclingRates as unknown) === ''
        ? null
        : Number(values.recyclingRates),
    disposalIncinerationRate: toNumberOrNull(values.disposalIncinerationRate),
    disposalLandfillRate: toNumberOrNull(values.disposalLandfillRate),
    secondaryDataReferenceSourceType: toNumberOrNull(values.secondaryDataReferenceSourceType),
    referenceRegionIdentifier: toNumberOrNull(values.referenceRegionIdentifier),
    referencesSamplingYear: toNumberOrNull(values.referencesSamplingYear),
    referenceDataPublicationYear: toNumberOrNull(values.referenceDataPublicationYear),
    referenceSourcingCountry: toNumberOrNull(values.referenceSourcingCountry),
    referenceTechnologyProxyContribution: toNumberOrNull(
      values.referenceTechnologyProxyContribution
    ),
  }
}

const rateSchema = Yup.number()
  .nullable()
  .transform((value, original) => (original === '' || original === null ? null : value))
  .typeError('Disposal rate must be between 0 and 100.')
  .min(0, 'Disposal rate must be between 0 and 100.')
  .max(100, 'Disposal rate must be between 0 and 100.')

export const formSchema = Yup.object()
  .shape({
    countryId: Yup.number()
      .nullable()
      .required('Country is required')
      .moreThan(0, 'Country is required'),
    productTaxonomyLevel: Yup.number()
      .nullable()
      .required('Taxonomy level is required')
      .moreThan(0, 'Taxonomy level is required'),
    productId: Yup.number()
      .nullable()
      .required('Product is required')
      .moreThan(0, 'Product is required'),
    recyclingRates: Yup.number()
      .nullable()
      .transform((value, original) => (original === '' || original === null ? null : value))
      .typeError('Recycling rate must be between 0 and 100.')
      .min(0, 'Recycling rate must be between 0 and 100.')
      .max(100, 'Recycling rate must be between 0 and 100.'),
    disposalIncinerationRate: rateSchema,
    disposalLandfillRate: rateSchema,
  })
  .test('disposal-rates', DISPOSAL_RATE_ERROR, function (values) {
    const incineration = values?.disposalIncinerationRate
    const landfill = values?.disposalLandfillRate
    if (incineration == null || landfill == null) return true
    if (incineration < 0 || incineration > 100 || landfill < 0 || landfill > 100) return true
    // The server uses decimal equality; round away binary addition noise, not a tolerance.
    return Number((incineration + landfill).toFixed(10)) === 100
      ? true
      : this.createError({ path: 'disposalLandfillRate', message: DISPOSAL_RATE_ERROR })
  })

// Mirrors PublishEndOfLifeValidator, so the page states the same reasons the server would, before
// the request goes out. Returns one message per unmet condition, empty when the record can publish.
export const getPublishBlockers = (values: IEndOfLife): string[] => {
  const blockers: string[] = []

  if (values.recyclingRates === null || values.recyclingRates === undefined) {
    blockers.push('Recycling rate is required to publish.')
  }

  if (!values.regionIdentifierId) {
    blockers.push('Location region is required to publish.')
  }
  const incineration = toNumberOrNull(values.disposalIncinerationRate)
  const landfill = toNumberOrNull(values.disposalLandfillRate)
  if (
    incineration === null ||
    landfill === null ||
    Number((incineration + landfill).toFixed(10)) !== 100
  ) {
    blockers.push(DISPOSAL_RATE_ERROR)
  }

  return blockers
}

export const blockerTab = (blockers: string[]): TabsEnum =>
  blockers[0].startsWith('Recycling rate')
    ? TabsEnum.RECYCLING_RATES
    : blockers[0].startsWith('Location region')
    ? TabsEnum.COUNTRY_INFO
    : TabsEnum.WASTE_DISPOSAL

export const canPublish = (values: IEndOfLife): boolean => getPublishBlockers(values).length === 0

// Entries are sorted because the form object is rebuilt by spreading, which does not preserve the
// record's key order.
const toComparableWireShape = (source: IEndOfLife): string => {
  const entries = Object.entries(source as unknown as Record<string, unknown>)
  return JSON.stringify(entries.sort(([a], [b]) => a.localeCompare(b)))
}

export const hasUnsavedChanges = (record: IEndOfLife, values: IEndOfLife): boolean =>
  toComparableWireShape(record) !== toComparableWireShape(values)
